import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { movies, hotRankings, latestMovies, movieCategories, Movie } from '@/mocks/movies';

function MovieCard({ movie, rank }: { movie: Movie; rank?: number }) {
  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block cursor-pointer"
    >
      <div className="relative overflow-hidden rounded-lg aspect-[2/3] mb-3">
        <img
          src={movie.image}
          alt={movie.title}
          title={`${movie.title} - 萝卜影视高清在线观看`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <span className="text-white text-xs font-medium whitespace-nowrap">{movie.description.slice(0, 40)}...</span>
        </div>
        {rank && (
          <div className="absolute top-2 left-2 w-7 h-7 rounded-md bg-primary-500 text-white text-xs font-bold flex items-center justify-center">
            {rank}
          </div>
        )}
        <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm rounded-md px-2 py-0.5 text-accent-400 text-xs font-semibold whitespace-nowrap">
          {movie.rating}
        </div>
      </div>
      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
        {movie.title}
      </h3>
      <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">
        {movie.year} · {movie.duration}
      </p>
    </Link>
  );
}

function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const heroMovieIds = ['movie-1', 'movie-4', 'movie-6'];
  const heroMovies = heroMovieIds.map((id) => movies.find((m) => m.id === id)!);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroMovies.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroMovies.length]);

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {heroMovies.map((movie, index) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-800 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={movie.image}
            alt={movie.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
        </div>
      ))}

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {heroMovies.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              index === currentSlide
                ? 'bg-primary-500 w-6'
                : 'bg-white/50 hover:bg-white/70'
            }`}
            aria-label={`切换到第${index + 1}张`}
          />
        ))}
      </div>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center z-10 px-4">
        <div className="w-full max-w-2xl">
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4 whitespace-nowrap">
            萝卜影视
          </h1>
          <p className="text-sm md:text-lg text-white/80 mb-8 max-w-lg mx-auto leading-relaxed">
            汇聚电影大全，最新电影上线与热门电影排行榜，电影在线观看免费高清
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-medium text-sm whitespace-nowrap transition-all duration-300 cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-movie-line"></i>
              </span>
              立即观影
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white rounded-full font-medium text-sm whitespace-nowrap transition-all duration-300 cursor-pointer border border-white/20"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-information-line"></i>
              </span>
              了解更多
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategoryNav() {
  return (
    <section className="w-full px-4 md:px-6 py-8">
      <div className="flex items-center gap-2 flex-wrap justify-center">
        {movieCategories.map((cat) => (
          <Link
            key={cat.id}
            to={`/category/${cat.id}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-primary-100/70 text-foreground-700 hover:text-primary-600 text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className={`${cat.icon} text-xs`}></i>
            </span>
            {cat.name}
          </Link>
        ))}
      </div>
    </section>
  );
}

function HotRankingSection() {
  const rankingMovies = hotRankings
    .map((r) => {
      const movie = movies.find((m) => m.id === r.movieId);
      return movie ? { ...r, movie } : null;
    })
    .filter(Boolean) as { rank: number; movieId: string; hotScore: number; movie: Movie }[];

  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/50">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
              热门电影排行榜
            </h2>
            <p className="text-sm text-foreground-500 mt-1">实时更新，发现最受欢迎的电影</p>
          </div>
          <Link
            to="/category/all"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
          >
            查看全部
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </span>
          </Link>
        </div>

        <div className="space-y-3">
          {rankingMovies.map(({ rank, movie, hotScore }) => (
            <Link
              key={movie.id}
              to={`/article/${movie.id}`}
              className="flex items-center gap-4 p-3 rounded-lg bg-background-50 hover:bg-background-200/50 transition-colors cursor-pointer group"
            >
              <div className={`w-8 h-8 rounded-md flex items-center justify-center text-sm font-bold whitespace-nowrap ${
                rank <= 3
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-200 text-foreground-600'
              }`}>
                {rank}
              </div>
              <div className="w-12 h-16 rounded overflow-hidden flex-shrink-0">
                <img src={movie.image} alt={movie.title} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                  {movie.title}
                </h3>
                <p className="text-xs text-foreground-500 mt-0.5 whitespace-nowrap">
                  {movie.year} · {movie.category === 'scifi' ? '科幻' : movie.category === 'action' ? '动作' : movie.category === 'comedy' ? '喜剧' : movie.category === 'romance' ? '爱情' : movie.category === 'suspense' ? '悬疑' : movie.category === 'animation' ? '动画' : movie.category === 'war' ? '战争' : '纪录片'} · {movie.rating}分
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-semibold text-accent-600 whitespace-nowrap">
                  {hotScore.toLocaleString()}
                </div>
                <div className="text-xs text-foreground-400 whitespace-nowrap">热度</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestSection() {
  const latestMovieData = latestMovies
    .map((id) => movies.find((m) => m.id === id))
    .filter(Boolean) as Movie[];

  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
              最新电影上线
            </h2>
            <p className="text-sm text-foreground-500 mt-1">新鲜出炉的热映大片，第一时间为您呈现</p>
          </div>
          <Link
            to="/category/all"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
          >
            查看全部
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </span>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
          {latestMovieData.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const faqs = [
    {
      q: '萝卜影视是否需要注册才能观看电影？',
      a: '不需要注册。萝卜影视致力于为用户提供便捷的观影体验，所有电影资源均可免费在线观看，无需注册或登录，打开网站即可畅享高清影视内容。',
    },
    {
      q: '萝卜影视支持哪些设备观看？',
      a: '萝卜影视全面支持电脑、平板、手机等多种设备，采用响应式设计，无论您使用什么设备，都能获得流畅的观影体验。推荐使用Chrome、Safari等主流浏览器。',
    },
    {
      q: '电影资源更新频率如何？',
      a: '萝卜影视每日更新电影资源，最新上映的电影会在第一时间上线。热门电影排行榜实时更新，确保您能够及时了解当前最受欢迎的电影动态。',
    },
    {
      q: '播放过程中出现卡顿怎么办？',
      a: '萝卜影视采用先进的视频加速技术，通常情况下播放流畅不缓冲。如果遇到卡顿，建议检查网络连接，或切换到更低分辨率。大部分电影支持多种清晰度选择。',
    },
  ];

  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/50">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
          常见问题
        </h2>
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <details key={index} className="group bg-background-50 rounded-lg p-5 cursor-pointer">
              <summary className="text-sm font-semibold text-foreground-900 list-none flex items-center justify-between">
                {faq.q}
                <span className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180">
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </summary>
              <p className="text-sm text-foreground-600 mt-3 leading-relaxed">{faq.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative w-full px-4 md:px-6 py-16 md:py-20 overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20theater%20interior%20with%20soft%20golden%20lighting%2C%20empty%20red%20velvet%20seats%2C%20large%20projection%20screen%2C%20luxurious%20atmosphere%2C%20warm%20amber%20and%20deep%20crimson%20tones%2C%20film%20projector%20light%20beam%2C%20cozy%20and%20inviting%20mood&width=1600&height=600&seq=cta-bg-home&orientation=landscape"
        alt="影院体验"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
      <div className="relative z-10 max-w-2xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
          开始您的观影之旅
        </h2>
        <p className="text-sm md:text-base text-white/80 mb-8 leading-relaxed">
          数千部电影资源，高清流畅播放，随时随地畅享影视盛宴
        </p>
        <Link
          to="/category/all"
          className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 hover:bg-accent-600 text-white rounded-full font-semibold text-sm whitespace-nowrap transition-all duration-300 cursor-pointer"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-play-circle-line"></i>
          </span>
          立即开始观影
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Organization"
        data={{
          name: '萝卜影视',
          url: 'https://luobo-movies.com',
          description: '萝卜影视汇聚电影大全，最新电影上线与热门电影排行榜，电影在线观看免费高清',
          sameAs: ['https://weibo.com/luobomovies', 'https://space.bilibili.com/luobomovies'],
        }}
      />
      <SchemaOrg
        type="WebSite"
        data={{
          name: '萝卜影视',
          url: 'https://luobo-movies.com',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://luobo-movies.com/category/all?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '萝卜影视 - 首页',
          description: '萝卜影视汇聚电影大全，最新电影上线与热门电影排行榜，电影在线观看免费高清',
          url: 'https://luobo-movies.com/',
          isPartOf: { '@id': 'https://luobo-movies.com/#website' },
        }}
      />
      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: [
            {
              '@type': 'Question',
              name: '萝卜影视是否需要注册才能观看电影？',
              acceptedAnswer: { '@type': 'Answer', text: '不需要注册。萝卜影视致力于为用户提供便捷的观影体验，所有电影资源均可免费在线观看，无需注册或登录。' },
            },
            {
              '@type': 'Question',
              name: '萝卜影视支持哪些设备观看？',
              acceptedAnswer: { '@type': 'Answer', text: '萝卜影视全面支持电脑、平板、手机等多种设备，采用响应式设计，推荐使用Chrome、Safari等主流浏览器。' },
            },
            {
              '@type': 'Question',
              name: '电影资源更新频率如何？',
              acceptedAnswer: { '@type': 'Answer', text: '萝卜影视每日更新电影资源，最新上映的电影会在第一时间上线。' },
            },
            {
              '@type': 'Question',
              name: '播放过程中出现卡顿怎么办？',
              acceptedAnswer: { '@type': 'Answer', text: '建议检查网络连接，或切换到更低分辨率。大部分电影支持多种清晰度选择。' },
            },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <HeroSection />
          <CategoryNav />
          <LatestSection />
          <HotRankingSection />
          <FAQSection />
          <CTASection />
        </main>
        <Footer />
      </div>
    </>
  );
}